/**
 * Operations Command Center · DEC-053.
 *
 * The operational front door of the platform. Answers four questions
 * at a glance:
 *   1. What changed today?
 *   2. What needs my attention?
 *   3. What work is in progress?
 *   4. What should I do next?
 *
 * Every widget on this page satisfies exactly one of three purposes:
 * INFORM · PRIORITIZE · LAUNCH. No charts. No analytics. No BI.
 *
 * All data is sourced from existing endpoints — this page orchestrates,
 * it does not replace.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Compass, Sun, Coffee, Phone, ListChecks, Lightbulb, WarningCircle,
  ArrowRight, Users, Truck, Factory, CloudRain, PencilLine,
  MagnifyingGlass, Plus, Sliders, CheckCircle, Circle, SunHorizon, Timer,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMorningOperationsProgress } from "@/lib/useMorningOperationsProgress";
import NewTaskDialog from "@/components/NewTaskDialog";
import AddOperationalNoteDialog from "@/components/AddOperationalNoteDialog";
import RecommendationList from "@/components/RecommendationList";

const WEATHER_LABEL = {
  clear: "Clear", rain: "Rain", snow: "Snow",
  storm: "Storm", extreme_cold: "Extreme Cold",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

/* ==================================================================
   Root
   ================================================================== */
export default function OperationsCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // DEC-054 · RC1 M1 · role guard · Ops Center is intended for operations
  // staff (Admin / Owner / Office Staff). Drivers who land here via a stale
  // link are redirected to the Dashboard.
  const forbidden = !!(user?.role && !["Administrator", "Owner", "Office Staff"].includes(user.role));
  useEffect(() => {
    if (forbidden) navigate("/dashboard", { replace: true });
  }, [forbidden, navigate]);
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState(null);
  const [openMessages, setOpenMessages] = useState(null);
  const [taskCounts, setTaskCounts] = useState(null);
  const [intel, setIntel] = useState(null);
  const [insights, setInsights] = useState(null);
  const [newTaskOpen, setNewTaskOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      api.get("/purchasing-recommendations"),
      api.get("/phone-messages", { params: { status: "open", limit: 200 } })
         .catch(() => api.get("/phone-messages", { params: { limit: 200 } })),
      api.get("/tasks/counts"),
      api.get("/morning-intelligence/today"),
      api.get("/fifo/insights"),
    ]);
    const [r, m, t, i, ins] = results.map((x) => x.status === "fulfilled" ? x.value.data : null);
    setRecs(r); setOpenMessages(m); setTaskCounts(t); setIntel(i);
    setInsights(ins);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const greeting = useMemo(() => {
    const hr = now.getHours();
    const g = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
    const name = (user?.full_name || user?.email || "").split(" ")[0] || "there";
    return `${g}, ${name}.`;
  }, [user]);

  const recSummary = recs?.summary || { BUY: 0, WATCH: 0, HOLD: 0 };

  // Operations Inbox count — total open messages, plus urgent/today band.
  const inboxSummary = useMemo(() => {
    const items = openMessages?.items || openMessages || [];
    const openOnly = (Array.isArray(items) ? items : []).filter((x) => x.status !== "closed");
    const urgent = openOnly.filter((x) => x.priority === "urgent").length;
    const today  = openOnly.filter((x) => x.priority === "today").length;
    return { total: openOnly.length, urgent, today };
  }, [openMessages]);

  // Alerts derived from FIFO insights (high/warn) and morning intelligence.
  const alerts = useMemo(() => {
    const rows = [];
    const priority = { high: 0, warn: 1, info: 2 };
    for (const it of (insights?.items || [])) {
      if (["warn", "high"].includes(it.severity)) {
        rows.push({
          key: `fifo-${it.code}`,
          severity: it.severity,
          label: it.title || it.code,
          detail: it.narrative || "",
          origin: "FIFO / Inventory",
          to: "/fifo",
        });
      }
    }
    const supp = (intel?.sections?.supplier_intel?.entries || []);
    for (const [i, e] of supp.entries()) {
      if (e.direction === "supplier_constraint" || e.direction === "against_buy") {
        rows.push({
          key: `supp-${i}`, severity: "warn",
          label: `Supplier · ${e.supplier || "—"}${e.product_code ? ` · ${e.product_code}` : ""}`,
          detail: e.note, origin: "Morning Brief · Supplier", to: "/brief",
        });
      }
    }
    if (intel?.sections?.fleet?.has_issue === true) {
      rows.push({
        key: "fleet", severity: "warn",
        label: "Fleet or staffing issue reported",
        detail: intel.sections.fleet.notes || "",
        origin: "Morning Brief · Fleet", to: "/brief",
      });
    }
    const weather = intel?.sections?.weather?.condition;
    if (["snow", "storm", "extreme_cold"].includes(weather)) {
      rows.push({
        key: "weather", severity: "warn",
        label: `Weather · ${WEATHER_LABEL[weather]}`,
        detail: intel.sections.weather.notes || "", origin: "Morning Brief · Weather", to: "/brief",
      });
    }
    return rows.sort((a, b) => priority[a.severity] - priority[b.severity]).slice(0, 8);
  }, [insights, intel]);

  if (loading || forbidden) {
    return <div className="p-8 text-sm text-slate-500">Loading Operations Command Center…</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto pb-16" data-testid="ops-center-page">
      {/* Header --------------------------------------------------- */}
      <header className="border-b border-slate-200 pb-4 mb-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <Compass size={22} weight="duotone" className="text-slate-700" />
          <h1 className="text-2xl font-semibold text-slate-900">Operations Command Center</h1>
          <span className="text-sm text-slate-500" data-testid="ops-greeting">· {greeting}</span>
          <button data-testid="ops-refresh" onClick={load}
                  className="ml-auto text-xs font-medium px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">
            Refresh
          </button>
        </div>
        <p className="text-sm text-slate-600 mt-1.5" data-testid="ops-philosophy">
          Your operational launchpad. Every panel is designed to inform, prioritize, or launch you into work.
        </p>
      </header>

      {/* PRIMARY · Continue Start the Day (v1.1 · Sprint 2 · PO 2026-07-31)
          The operational heartbeat. Sits above every other card so
          managers see immediately whether Morning Operations is done. */}
      <StartTheDayCard />

      {/* Recommendation Engine · compact urgent feed (Sprint 3D · PO 2026-08-01)
          Only HIGH + MEDIUM urgency surface on the OCC. Full list lives
          on the Morning Brief and Purchasing workspace. */}
      <UrgentRecommendationsSection />

      {/* Top row · Morning Brief · Operations Inbox · Active Work · Purchasing */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        <MorningBriefCard intel={intel} recSummary={recSummary} insights={insights} />
        <OperationsInboxCard summary={inboxSummary} />
        <ActiveWorkCard counts={taskCounts} onNewTask={() => setNewTaskOpen(true)} />
        <PurchasingCard summary={recSummary} />
      </div>

      {/* Middle row · Alerts */}
      <div className="grid grid-cols-1 gap-4 mb-5">
        <AlertsCard alerts={alerts} />
      </div>

      {/* Quick Actions ------------------------------------------- */}
      <QuickActions onNewTask={() => setNewTaskOpen(true)} />

      {newTaskOpen ? (
        <NewTaskDialog onClose={() => setNewTaskOpen(false)}
                       onCreated={() => { setNewTaskOpen(false); load(); }} />
      ) : null}
    </div>
  );
}

/* ==================================================================
   Start the Day card — v1.1 · Sprint 2 · PO 2026-07-31
   ================================================================== */
const STD_STEPS = [
  { key: "price_notification",      label: "Price Notification",   route: "/price-notifications" },
  { key: "daily_readings",          label: "Daily Readings",       route: "/start-the-day/daily-readings" },
  { key: "tank_inventory",          label: "Tank Inventory",       route: "/start-the-day/tank-inventory" },
  { key: "reconciliation",          label: "Reconciliation",       route: "/start-the-day/reconciliation" },
];

/* ==================================================================
   Urgent Recommendations · Sprint 3D · PO 2026-08-01
   Compact urgent feed for the Operations Command Center.
   Filters to HIGH + MEDIUM urgency (LOW/WATCH stay on Brief).
   ================================================================== */
function UrgentRecommendationsSection() {
  return (
    <div className="mb-5">
      <RecommendationList
        title="Urgent Recommendations"
        testidPrefix="ops-recs"
        showUrgencies={["HIGH", "MEDIUM"]}
        compact
        emptyMessage="No urgent recommendations right now."
      />
    </div>
  );
}

function StartTheDayCard() {
  const nav = useNavigate();
  const prog = useMorningOperationsProgress();
  const [state, setState] = useState(null);
  const [stats, setStats] = useState(null);
  const [notesCount, setNotesCount] = useState(0);
  const [noteOpen, setNoteOpen] = useState(false);

  const refreshNotes = useCallback(() => {
    api.get("/operational-notes")
      .then(({ data }) => setNotesCount(data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => { refreshNotes(); }, [refreshNotes]);

  // Full state (for step details) — separate from the sidebar hook.
  useEffect(() => {
    let cancelled = false;
    api.get("/morning-operations/state")
      .then(({ data }) => { if (!cancelled) setState(data); })
      .catch(() => {});
    api.get("/morning-operations/stats")
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [prog.completed, prog.overallStatus]);

  const isComplete = prog.overallStatus === "COMPLETE";
  const nextStep = useMemo(() => {
    if (!state) return STD_STEPS[0];
    return STD_STEPS.find((s) => (state.steps?.[s.key]?.status || "NOT_STARTED") !== "COMPLETE") || null;
  }, [state]);
  const summary = useMemo(() => (state ? computeCompletionSummary(state) : null), [state]);
  const pct = Math.round((prog.completed / prog.total) * 100);
  const briefDate = state?.brief_date;
  const dateLabel = briefDate ? formatFullDate(briefDate) : "Today";

  if (isComplete && summary) {
    return (
      <>
      <div
        data-testid="ops-card-start-the-day-complete"
        className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-5"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-emerald-600/10 p-2 text-emerald-700">
            <CheckCircle size={22} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Morning Operations Complete</div>
            <div className="font-display text-xl font-semibold text-emerald-900">{dateLabel}</div>
            <p className="mt-1 text-sm text-emerald-800" data-testid="ops-std-complete-message">
              Today's operations have been prepared.
              {summary.brief_generated ? " Morning Brief generated successfully." : " Morning Brief has not been generated yet."}
            </p>
          </div>
          <button
            onClick={() => nav("/start-the-day")}
            data-testid="ops-std-view-summary"
            className="text-sm text-emerald-800 hover:text-emerald-900"
          >
            View summary →
          </button>
        </div>
        {notesCount > 0 ? (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white/70 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-900"
               data-testid="ops-std-notes-badge">
            <PencilLine size={11} weight="fill" />
            {notesCount} operator {notesCount === 1 ? "note" : "notes"} today
          </div>
        ) : null}
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
          <SummaryCell label="Completed by" value={summary.completed_by || "—"} testid="std-sum-by" />
          <SummaryCell label="Completed" value={summary.completed_at ? formatTime(summary.completed_at) : "—"} testid="std-sum-at" />
          <SummaryCell label="Duration" value={summary.duration_label} testid="std-sum-duration" />
          <SummaryCell label="Morning Brief Generated" value={summary.brief_generated ? "Yes" : "No"} testid="std-sum-brief" />
        </dl>
        <div className="mt-4 border-t border-emerald-200/70 pt-3">
          <button
            onClick={() => setNoteOpen(true)}
            data-testid="ops-std-add-note"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 hover:text-emerald-900"
          >
            <PencilLine size={12} weight="fill" />
            Add operational note
          </button>
        </div>
      </div>
      <AddOperationalNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        onSaved={refreshNotes}
        testidPrefix="ops-add-note"
      />
      </>
    );
  }

  return (
    <>
    <div
      data-testid="ops-card-start-the-day"
      className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-5"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-amber-500/10 p-2 text-amber-700">
          <SunHorizon size={22} weight="fill" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">Start the Day</div>
          <div className="font-display text-xl font-semibold text-amber-900">{dateLabel}</div>
          {notesCount > 0 ? (
            <div className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-amber-900"
                 data-testid="ops-std-notes-badge">
              <PencilLine size={11} weight="fill" />
              {notesCount} operator {notesCount === 1 ? "note" : "notes"} today
            </div>
          ) : null}
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-bold text-amber-900" data-testid="ops-std-progress">
            {prog.completed}/{prog.total}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-amber-700">Complete</div>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-amber-200/70">
        <div
          data-testid="ops-std-progress-bar"
          className="h-full bg-amber-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {/* Next step */}
      <div className="mt-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-amber-800">Next Step</div>
          <div className="font-display text-base font-semibold text-amber-900" data-testid="ops-std-next-step">
            {nextStep ? nextStep.label : "All steps complete"}
          </div>
        </div>
        <button
          onClick={() => nav(nextStep?.route || "/start-the-day")}
          data-testid="ops-std-continue"
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700"
        >
          {prog.overallStatus === "IN_PROGRESS" ? "Continue" : "Start the Day"}
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
      {/* Trends strip · Today · Yesterday · 30-day Average */}
      <dl
        data-testid="ops-std-trends"
        className="mt-4 grid grid-cols-3 gap-4 border-t border-amber-200/70 pt-3 text-xs"
      >
        <TrendCell
          label="Today"
          value={`${prog.completed} / ${prog.total} Complete`}
          testid="ops-std-trend-today"
        />
        <TrendCell
          label="Yesterday"
          value={
            stats?.yesterday
              ? `Completed ${formatTime(stats.yesterday.completed_at)}`
              : "No completion recorded"
          }
          testid="ops-std-trend-yesterday"
        />
        <TrendCell
          label={stats?.sample_size ? `Average (${stats.sample_size}-day)` : "Average (30-day)"}
          value={
            stats?.average_30d_minutes != null
              ? `${stats.average_30d_minutes} minutes`
              : "Not enough history yet"
          }
          testid="ops-std-trend-average"
        />
      </dl>
      <div className="mt-3 flex justify-end">
        <button
          onClick={() => setNoteOpen(true)}
          data-testid="ops-std-add-note"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:text-amber-900"
        >
          <PencilLine size={12} weight="fill" />
          Add operational note
        </button>
      </div>
    </div>
    <AddOperationalNoteDialog
      open={noteOpen}
      onOpenChange={setNoteOpen}
      onSaved={refreshNotes}
      testidPrefix="ops-add-note"
    />
    </>
  );
}

function TrendCell({ label, value, testid }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-amber-800">{label}</dt>
      <dd className="font-semibold text-amber-900" data-testid={testid}>{value}</dd>
    </div>
  );
}

function SummaryCell({ label, value, testid }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-emerald-700">{label}</dt>
      <dd className="font-semibold text-emerald-900" data-testid={testid}>{value}</dd>
    </div>
  );
}

function computeCompletionSummary(state) {
  const steps = state?.steps || {};
  const stamps = Object.values(steps)
    .map((s) => s?.completed_at)
    .filter(Boolean)
    .map((iso) => new Date(iso));
  const first = stamps.length ? new Date(Math.min(...stamps.map((d) => d.getTime()))) : null;
  const last  = stamps.length ? new Date(Math.max(...stamps.map((d) => d.getTime()))) : null;
  const last_step_key = "reconciliation";  // Sprint 10 · 2026-08-06 · Morning Brief is now the outcome, not a step.
  const last_step = steps[last_step_key];
  const completed_by = last_step?.completed_by
    || Object.values(steps).reverse().find((s) => s?.completed_by)?.completed_by
    || null;
  const completed_at = last ? last.toISOString() : null;
  const duration_ms = first && last ? (last.getTime() - first.getTime()) : 0;
  return {
    completed_by,
    completed_at,
    duration_label: formatDuration(duration_ms),
    brief_generated: steps[last_step_key]?.status === "COMPLETE",
  };
}

function formatFullDate(iso) {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}
function formatTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
}
function formatDuration(ms) {
  if (!ms || ms < 60000) return "under a minute";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
  const h = Math.floor(mins / 60);
  const m = mins - h * 60;
  return `${h}h ${m}m`;
}

/* ==================================================================
   Cards
   ================================================================== */
function Card({ title, icon: Icon, testId, to, actionLabel, tone = "default", children }) {
  const border = tone === "warn" ? "border-amber-300" : tone === "danger" ? "border-rose-300" : "border-slate-200";
  return (
    <section data-testid={testId}
             className={`rounded-lg border ${border} bg-white flex flex-col overflow-hidden`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        {Icon ? <Icon size={16} className="text-slate-500" /> : null}
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {to ? (
          <Link to={to} data-testid={`${testId}-action`}
                className="ml-auto text-[11px] font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
            {actionLabel || "Open"} <ArrowRight size={11} weight="bold" />
          </Link>
        ) : null}
      </div>
      <div className="p-4 flex-1">{children}</div>
    </section>
  );
}

function StatChip({ n, label, tone = "slate", testId }) {
  const map = {
    amber:  "bg-amber-100 text-amber-900 border-amber-300",
    sky:    "bg-sky-100 text-sky-900 border-sky-300",
    slate:  "bg-slate-100 text-slate-700 border-slate-300",
    rose:   "bg-rose-100 text-rose-800 border-rose-300",
    emerald:"bg-emerald-100 text-emerald-800 border-emerald-300",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-300",
  };
  return (
    <div data-testid={testId} className={`rounded-md border ${map[tone]} px-3 py-2 text-center`}>
      <div className="text-xl font-semibold tabular-nums">{n}</div>
      <div className="text-[10px] uppercase tracking-wide font-medium">{label}</div>
    </div>
  );
}

function MorningBriefCard({ intel, recSummary, insights }) {
  const prepared = intel?.status === "prepared";
  const alertCount = (insights?.items || []).filter((i) => ["warn", "high"].includes(i.severity)).length;
  return (
    <Card title="Morning Brief" icon={Sun} testId="ops-card-brief"
          to="/brief" actionLabel="View Full Morning Brief">
      {prepared ? (
        <p className="text-xs text-emerald-700 inline-flex items-center gap-1 mb-2" data-testid="ops-brief-prepared">
          <CheckCircle size={12} weight="fill" />
          Prepared by <b className="ml-0.5">{intel.prepared_by_name}</b>
        </p>
      ) : (
        <p className="text-xs text-amber-800 inline-flex items-center gap-1 mb-2" data-testid="ops-brief-nudge">
          <Circle size={10} /> Not yet prepared today
        </p>
      )}
      <p className="text-sm text-slate-800">
        <b className="text-amber-800 tabular-nums">{recSummary.BUY}</b> ORDER ·{" "}
        <b className="text-sky-800 tabular-nums">{recSummary.WATCH}</b> WATCH ·{" "}
        <b className="text-slate-700 tabular-nums">{recSummary.HOLD}</b> WAIT
      </p>
      <p className="text-xs text-slate-500 mt-1">
        {alertCount === 0 ? "No open engine alerts." : `${alertCount} engine alert${alertCount === 1 ? "" : "s"}.`}
      </p>
    </Card>
  );
}

function OperationsInboxCard({ summary }) {
  const tone = summary.urgent > 0 ? "danger" : summary.total > 0 ? "warn" : "default";
  const alertLevel = summary.urgent > 0 ? "rose" : summary.total > 0 ? "amber" : null;
  const linkTo = summary.total > 0 ? "/messages?open=1" : "/messages";
  return (
    <Card title="Operations Inbox" icon={Phone} testId="ops-card-inbox"
          to={linkTo} actionLabel="Open Inbox" tone={tone}>
      {/* PO hot-fix · prominent alert badge whenever open messages exist */}
      {summary.total > 0 ? (
        <Link to={linkTo} data-testid="ops-inbox-alert"
              className={`mb-3 flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold ${alertLevel === "rose" ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-amber-100 text-amber-900 border-amber-300"} hover:brightness-95`}>
          <WarningCircle size={12} weight="fill" />
          <span data-testid="ops-inbox-alert-count">
            {summary.total} open phone message{summary.total === 1 ? "" : "s"} awaiting action
          </span>
          <ArrowRight size={11} weight="bold" className="ml-auto" />
        </Link>
      ) : null}
      <div className="grid grid-cols-3 gap-2">
        <StatChip n={summary.total}  label="Open"    tone="slate"  testId="ops-inbox-total" />
        <StatChip n={summary.urgent} label="Urgent"  tone="rose"   testId="ops-inbox-urgent" />
        <StatChip n={summary.today}  label="Today"   tone="amber"  testId="ops-inbox-today" />
      </div>
      {summary.total === 0 ? (
        <div className="mt-3 text-center" data-testid="ops-inbox-empty">
          <p className="text-xs text-slate-500 mb-2">Inbox is clear.</p>
          <Link to="/messages?new=1" data-testid="ops-inbox-new"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800">
            <Plus size={11} weight="bold" /> New Phone Message
          </Link>
        </div>
      ) : (
        <div className="mt-3">
          <Link to="/messages?new=1" data-testid="ops-inbox-new"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 hover:text-slate-900">
            <Plus size={11} weight="bold" /> New Phone Message
          </Link>
        </div>
      )}
    </Card>
  );
}

function ActiveWorkCard({ counts, onNewTask }) {
  const c = counts || { open: 0, in_progress: 0, overdue: 0, due_today: 0 };
  const tone = c.overdue > 0 ? "danger" : c.due_today > 0 ? "warn" : "default";
  return (
    <Card title="Active Work" icon={ListChecks} testId="ops-card-work"
          to="/tasks" actionLabel="Open Tasks" tone={tone}>
      <div className="grid grid-cols-2 gap-2">
        <StatChip n={c.open}        label="Open"        tone="slate"   testId="ops-work-open" />
        <StatChip n={c.in_progress} label="In Progress" tone="indigo"  testId="ops-work-inprogress" />
        <StatChip n={c.due_today}   label="Due Today"   tone="amber"   testId="ops-work-duetoday" />
        <StatChip n={c.overdue}     label="Overdue"     tone="rose"    testId="ops-work-overdue" />
      </div>
      <button data-testid="ops-work-newtask" onClick={onNewTask}
              className="mt-3 text-[11px] font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
        <Plus size={11} weight="bold" /> New task
      </button>
    </Card>
  );
}

function PurchasingCard({ summary }) {
  const tone = summary.BUY > 0 ? "warn" : "default";
  return (
    <Card title="Purchasing Intelligence" icon={Lightbulb} testId="ops-card-purchasing"
          to="/purchasing" actionLabel="Open Recommendations" tone={tone}>
      <div className="grid grid-cols-3 gap-2">
        <StatChip n={summary.BUY}   label="Order" tone="amber"  testId="ops-buy-order" />
        <StatChip n={summary.WATCH} label="Watch" tone="sky"    testId="ops-buy-watch" />
        <StatChip n={summary.HOLD}  label="Wait"  tone="slate"  testId="ops-buy-wait" />
      </div>
      <p className="text-xs text-slate-500 mt-3">
        {summary.BUY === 0
          ? "No orders recommended right now."
          : `${summary.BUY} product${summary.BUY === 1 ? "" : "s"} recommended for order.`}
      </p>
    </Card>
  );
}

function AlertsCard({ alerts }) {
  return (
    <Card title="Operational Alerts" icon={WarningCircle} testId="ops-card-alerts">
      {alerts.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="ops-alerts-empty">
          No actionable alerts right now.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="ops-alerts-list">
          {alerts.map((a) => (
            <li key={a.key} className="py-2 flex items-start gap-2 text-sm"
                data-testid={`ops-alert-${a.key}`}>
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${a.severity === "high" ? "bg-rose-500" : "bg-amber-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">{a.label}</span>
                  <span className="text-[11px] text-slate-500">· {a.origin}</span>
                </div>
                {a.detail ? <p className="text-slate-600 line-clamp-2">{a.detail}</p> : null}
              </div>
              <Link to={a.to} className="text-[11px] font-medium text-slate-600 hover:text-slate-900 shrink-0">
                Open →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function QuickActions({ onNewTask }) {
  const actions = [
    { key: "start-the-day", to: "/start-the-day", icon: Coffee,         label: "Start the Day" },
    { key: "new-message",     to: "/messages?new=1",  icon: Phone,          label: "New Phone Message" },
    { key: "new-task",        onClick: onNewTask,     icon: ListChecks,     label: "New Task" },
    { key: "customer-lookup", to: "/customers",       icon: MagnifyingGlass,label: "Customer Lookup" },
    { key: "recommendations", to: "/purchasing",      icon: Lightbulb,      label: "Purchasing Recommendations" },
    { key: "thresholds",      to: "/admin/thresholds",icon: Sliders,        label: "Threshold Administration" },
  ];
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ops-quick-actions">
      <h2 className="text-sm font-semibold text-slate-900 mb-3">Quick Actions</h2>
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => a.onClick ? (
          <button key={a.key} onClick={a.onClick}
                  data-testid={`ops-quick-${a.key}`}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 px-3 py-2 text-sm text-slate-800">
            <a.icon size={14} className="text-slate-500" /> {a.label}
          </button>
        ) : (
          <Link key={a.key} to={a.to}
                data-testid={`ops-quick-${a.key}`}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 px-3 py-2 text-sm text-slate-800">
            <a.icon size={14} className="text-slate-500" /> {a.label}
          </Link>
        ))}
      </div>
    </section>
  );
}
